import React from 'react';

// PageErrorBoundary — catches render-time exceptions inside a protected
// route so a single bad line never blanks the whole app. We deliberately
// avoid using any modern-only browser API in this file so the boundary
// itself never crashes on the browsers we most want to catch.
export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error: error };
  }

  componentDidCatch(error, info) {
    // Surface to the browser console so the team can capture it via
    // regular dev-tools reporting; do not send anywhere else.
    console.error('[PageErrorBoundary]', error, info && info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Unexpected error';
      return (
        <div className="max-w-2xl" data-testid="page-error-boundary">
          <div className="border-l-4 border-rose-500 bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-rose-600 font-semibold">
              Page error
            </div>
            <div className="mt-1 text-lg font-semibold text-indie-ink">
              This page hit a runtime error and stopped rendering.
            </div>
            <div className="mt-2 text-[12px] text-indie-ink/70">
              We caught it here so the rest of Command Center still works. Copy the message
              below, then reload — if it keeps happening, ping the team with the details.
            </div>
            <pre className="mt-3 whitespace-pre-wrap text-[11px] text-indie-ink bg-indie-cream/60 p-3 border-l-2 border-rose-500/60 font-mono">
              {msg}
            </pre>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={this.handleReset}
                className="text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold hover:underline"
                data-testid="page-error-retry"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-[11px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold hover:underline"
                data-testid="page-error-reload"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
